import { useForm } from "react-hook-form";
import { Controller } from "react-hook-form";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import FieldError from "../FieldError";
import SelectOrderStates from "../Selects/OrderStates";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale } from "react-datepicker";
import { useTranslation } from "react-i18next";
import fr from "date-fns/locale/fr";
import en from "date-fns/locale/en-US";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/pro-regular-svg-icons";

const SearchPurchaseOrder = ({ formData, setFormData }) => {
  const form = useForm();
  const partner = form.watch("partner");
  const pieceNumber = form.watch("pieceNumber");
  const endDate = form.watch("endDate");
  const startDate = form.watch("startDate");
  const authUser = useSelector((state) => state.auth);
  const isAdmin = authUser?.user?.roles.includes("ROLE_ADMIN");
  const { t, i18n } = useTranslation();

  if (i18n.language === "en") {
    registerLocale("en", en);
  } else {
    registerLocale("fr", fr);
  }

  const formSubmitBtn = useRef();
  const {
    setError,
    formState: { errors },
  } = useForm();

  const handleSubmit = () => {
    formSubmitBtn.current.click();
  };

  let timeout;

  useEffect(() => {
    timeout = setTimeout(() => {
      if (formData && pieceNumber !== "") {
        handleSubmit();
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [pieceNumber]);

  useEffect(() => {
    timeout = setTimeout(() => {
      if (formData || partner !== "") {
        handleSubmit();
      }
    }, 350);
    return () => clearTimeout(timeout);
  }, [partner]);

  useEffect(() => {
    if (startDate || endDate) {
      let end = dayjs(endDate) || dayjs();
      let start = dayjs(startDate);
      if (end.isBefore(start)) {
        setError("endDate", {
          type: "manual",
          icon: (
            <FontAwesomeIcon icon={faTriangleExclamation} className="pr-2" />
          ),
          message: t("settings.orders.search.message_date"),
        });
      } else {
        handleSubmit();
        setError("endDate", {
          type: "manual",
          icon: "",
          message: "",
        });
      }
    }
    handleSubmit();
  }, [startDate, endDate]);

  return (
    <form
      onSubmit={form.handleSubmit((data) => setFormData(data))}
      className="grid grid-cols-12 p-4 px-6 bg-white shadow lg:shadow-none pb-8 lg:bg-gray-100 rounded-lg"
      autoComplete="off"
    >
      {errors.endDate && (
        <div className="col-span-12 text-red-600">
          {errors.endDate.icon}
          {errors.endDate.message}
        </div>
      )}

      <div className="col-span-12 grid grid-cols-12 gap-4">
        {isAdmin && (
          <div className="col-span-12 lg:col-span-6">
            <label>{t("settings.orders.search.startDate")}</label>
            <div className="flex gap-4">
              <div className="w-full">
                <Controller
                  control={form.control}
                  name="startDate"
                  render={({ field: { onChange, ref, value } }) => (
                    <DatePicker
                      onChange={onChange}
                      selected={value}
                      inputRef={ref}
                      dateFormat={"dd/MM/yyyy"}
                      className="text-center w-full"
                      locale={i18n.language}
                      placeholderText={t("settings.orders.search.fromDate")}
                      aria-invalid={
                        form.formState?.errors?.startDate ? true : false
                      }
                    />
                  )}
                />
                <FieldError error={form.formState?.errors?.startDate} />
              </div>

              <div className=" w-full">
                <Controller
                  control={form.control}
                  name="endDate"
                  render={({ field: { onChange, ref, value } }) => (
                    <DatePicker
                      onChange={onChange}
                      selected={value}
                      inputRef={ref}
                      dateFormat={"dd/MM/yyyy"}
                      className="text-center w-full"
                      locale={i18n.language}
                      placeholderText={t("settings.orders.search.endDate")}
                      aria-invalid={
                        form.formState?.errors?.endDate ? true : false
                      }
                    />
                  )}
                />
                <FieldError error={form.formState?.errors?.endDate} />
              </div>
            </div>
          </div>
        )}

        <div className="col-span-12 lg:col-span-6">
          <label>{t("settings.orders.search.state")}</label>
          <Controller
            control={form.control}
            name="state"
            render={({ field: { onChange, value } }) => (
              <SelectOrderStates
                onChange={(e) => {
                  onChange(e);
                  handleSubmit();
                }}
                value={value}
                className={"py-1 w-full"}
              />
            )}
          />
        </div>

        <div className={`col-span-12 lg:col-span-6`}>
          <label>{t("settings.orders.search.pieceNumber")}</label>
          <input
            type="text"
            className="w-full"
            {...form.register("pieceNumber")}
            name="pieceNumber"
          />
        </div>

        {isAdmin && (
          <>
            <div className="col-span-12 lg:col-span-6">
              <label>{t("settings.orders.header.partner")}</label>
              <input
                type="text"
                {...form.register("partner")}
                name="partner"
                className="w-full"
              />
            </div>
          </>
        )}
      </div>

      <input type="submit" className="hidden" ref={formSubmitBtn} />
    </form>
  );
};

export default SearchPurchaseOrder;
